import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useComplianceDocumentLastSavedLabel } from './useComplianceDocumentLastSavedLabel'
import type {
  ComplianceDocumentI18nNamespace,
  ComplianceDocumentTranslationKeyPrefix,
} from './complianceDocumentEditorTypes'

const SAVING_INDICATOR_DELAY_MS = 300

type ComplianceDocumentLastSavedIndicatorProps = {
  isAutosaving: boolean
  lastSavedAt: Date | null
  translationKeyPrefix: ComplianceDocumentTranslationKeyPrefix
  i18nNamespace: ComplianceDocumentI18nNamespace
}

export function ComplianceDocumentLastSavedIndicator({
  isAutosaving,
  lastSavedAt,
  translationKeyPrefix,
  i18nNamespace,
}: ComplianceDocumentLastSavedIndicatorProps) {
  const { t } = useTranslation(i18nNamespace)
  const lastSavedLabel = useComplianceDocumentLastSavedLabel(
    lastSavedAt,
    translationKeyPrefix,
    i18nNamespace
  )
  const [showSavingSpinner, setShowSavingSpinner] = useState(false)

  useEffect(() => {
    if (!isAutosaving) {
      setShowSavingSpinner(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setShowSavingSpinner(true)
    }, SAVING_INDICATOR_DELAY_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [isAutosaving])

  return (
    <div
      className='flex h-9 items-center justify-end'
      aria-live='polite'
      aria-busy={isAutosaving}
    >
      {showSavingSpinner ? (
        <Loader2
          className='size-4 shrink-0 animate-spin text-muted-foreground'
          aria-hidden
        />
      ) : null}
      <span className='text-xs text-muted-foreground'>
        {lastSavedLabel ?? t(`${translationKeyPrefix}.autosaved`)}
      </span>
    </div>
  )
}
